import React, {
  useCallback, useRef, useState, useEffect,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Row, Col,
} from 'reactstrap';

import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import Checkbox from '@shared/components/input/Checkbox';
import InputField from '@shared/components/input/InputField';
import RadioInput from '@shared/components/input/RadioInput';

import { useReferralStepRequests } from '../../guest/GuestReferralContext';
import SpecialtiesServicesSkeleton from '../skeleton-loading/SpecialtiesServicesSkeleton';

import '../../SendReferralOrderPageStyle.scss';

type SpecialtiesServicesProps = {
  selectedSpecialties: any;
  initialSpecialties: any;
  setSelectedSpecialties: (e: any) => void;
  formData: any;
  setFormData: (e: any) => void;
  errors: any;
  isCompleted: boolean;
  open: string;
  setNewCheckedSpecialties: (e: any) => void;
  setUncheckedSpecialties: (e: any) => void;
};

const SpecialtiesServices = ({
  selectedSpecialties, setSelectedSpecialties,
  formData, setFormData, errors, isCompleted, open,
  setUncheckedSpecialties, setNewCheckedSpecialties,
  initialSpecialties,
}: SpecialtiesServicesProps) => {
  const dispatch = useDispatch<any>();
  const requests = useReferralStepRequests();
  const i18nReferralReason = LocaleService.getTranslations('referrals.sendReferral.forms.reasonForReferral');
  const [ specialtiesServices, setSpecialtiesServices ] = useState<any>([]);
  const [ deletedSelectedSpecialties, setDeletedSelectedSpecialties ] = useState<any>([]);
  const [ loading, setLoading ] = useState(true);
  const reloadTimeout = useRef<any>(null);

  const onLoadSpecialtiesAndServices = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(requests.getSpecialtiesAndServicesRequest()).$promise;
      const resData = result?.data?.data ?? [];
      if (deletedSelectedSpecialties.length > 0) {
        const mergedData = [ ...deletedSelectedSpecialties, ...resData ];
        setSpecialtiesServices(mergedData);
        return;
      }
      setSpecialtiesServices(resData);
    } catch (error) {
      handleError(error);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  }, [ deletedSelectedSpecialties, dispatch, requests ]);

  const handleCheckboxChange = (specialtyData: any, childrenIds: string[] = []) => {
    const id = specialtyData?.id;
    const name = specialtyData?.name;
    const parentId = specialtyData?.parent_id;
    const parsedServiceIds = childrenIds.map((s: any) => s.id);
    const parsedSubServiceIds: any = [];

    childrenIds.forEach((child: any) => {
      child.sub_services.forEach((sub: any) => {
        parsedSubServiceIds.push(sub.id);
      });
    });

    const isInInitial = (specialtyId: any) => initialSpecialties
      .some((specialty: any) => specialty.specialties_id === specialtyId);

    const updateUncheckedAndNewChecked = (isSelected: boolean) => {
      if (isSelected) {
        // Handle unchecking
        if (isInInitial(id)) {
          // get the data from initial specialties that has referral_service id
          // then store it in setUncheckedSpecialties
          const initialSpecialty = initialSpecialties.find(
            (specialty: any) => specialty.specialties_id === id,
          );
          if (initialSpecialty) {
            setUncheckedSpecialties((prev: any) => (prev
              .some((specialty: any) => specialty.specialties_id === id)
              ? prev
              : [ ...prev, initialSpecialty ]),
            );
          }
        }
        // Remove from newly added if exists
        setNewCheckedSpecialties((prev: any) => prev.filter(
          (specialty: any) => specialty.specialties_id !== id
              && !parsedServiceIds.includes(specialty.specialties_id)
              && !parsedSubServiceIds.includes(specialty.specialties_id),
        ));
        // Add children from `initialSpecialties` to `setUncheckedSpecialties`
        parsedServiceIds.forEach((childId) => {
          const initialChild = initialSpecialties.find(
            (specialty: any) => specialty.specialties_id === childId,
          );
          if (initialChild) {
            setUncheckedSpecialties((prev: any) => (prev
              .some((specialty: any) => specialty.specialties_id === childId)
              ? prev
              : [ ...prev, initialChild ]),
            );
          }
        });
        // Add sub services from `initialSpecialties` to `setUncheckedSpecialties`
        parsedSubServiceIds.forEach((childId: string) => {
          const initialChild = initialSpecialties.find(
            (specialty: any) => specialty.specialties_id === childId,
          );
          if (initialChild) {
            setUncheckedSpecialties((prev: any) => (prev
              .some((specialty: any) => specialty.specialties_id === childId)
              ? prev
              : [ ...prev, initialChild ]),
            );
          }
        });
        return;
      }
      // Handle checking
      if (!isInInitial(id)) {
        const newSpecialty = {
          specialties_id: id,
          specialties_name: name,
          specialties: name,
          specialties_parent_id: parentId || null,
          remarks: formData[id] || null,
        };
        setNewCheckedSpecialties((prev: any) => (prev
          .some((specialty: any) => specialty.specialties_id === id)
          ? prev
          : [ ...prev, newSpecialty ]),
        );
      }
      // Remove from unchecked if exists
      setUncheckedSpecialties((prev: any) => prev.filter(
        (specialty: any) => specialty.specialties_id !== id
            && !parsedServiceIds.includes(specialty.specialties_id)
            && !parsedSubServiceIds.includes(specialty.specialties_id),
      ));
    };

    setSelectedSpecialties((prevSelectedSpecialties: any) => {
      const isSelected = prevSelectedSpecialties
        .some((specialty: any) => specialty.specialties_id === id);

      updateUncheckedAndNewChecked(isSelected);

      if (isSelected) {
        // setFormData((prevData: any) => ({ ...prevData, [id]: '' }));
        setFormData((prevData: any) => {
          const newData = { ...prevData };
          delete newData[id];
          parsedServiceIds.forEach((childId: any) => delete newData[childId]);
          parsedSubServiceIds.forEach((subId: any) => delete newData[subId]);
          return newData;
        });

        return prevSelectedSpecialties
          .filter((specialty: any) => specialty.specialties_id !== id
        && !parsedServiceIds.includes(specialty.specialties_id)
        && !parsedSubServiceIds.includes(specialty.specialties_id));
      }

      const newSpecialty = {
        specialties_id: id,
        specialties_name: name,
        specialties: name,
        specialties_parent_id: parentId || null,
        remarks: formData[id] || null,
      };
      const newSelectedSpecialties = [ ...prevSelectedSpecialties, newSpecialty ];

      return newSelectedSpecialties;
    });
  };

  const handleRemarksChange = (id: string, value: string) => {
    // max length is only 100
    if (value.length > 100) return;

    // Update form data with the new remark
    setFormData((prevData: any) => ({ ...prevData, [id]: value }));

    // Update remarks for newly added specialties
    setNewCheckedSpecialties((prevNewCheckedSpecialties: any) => prevNewCheckedSpecialties
      .map((specialty: any) => (specialty.specialties_id === id
        ? { ...specialty, remarks: value } // Update the remarks for the matched specialty
        : specialty),
      ));
  };

  useEffect(() => {
    if (specialtiesServices.length === 0 && open === '4') {
      clearTimeout(reloadTimeout.current);
      reloadTimeout.current = setTimeout(onLoadSpecialtiesAndServices, 500);
    }

    return () => {
      clearTimeout(reloadTimeout.current);
    };
  }, [ onLoadSpecialtiesAndServices, open, specialtiesServices.length ]);

  useEffect(() => {
    if (open !== '4') return;
    const removedSpecialties = selectedSpecialties.filter((item: any) => item.details === null
    && !item.parent_id);
    const removedServices = selectedSpecialties.filter((item: any) => item.details === null
    && item.parent_id);

    // Map services to specialties recursively
    const mapServices = (services: any, parentId = null) => {
      const res: any = [];
      services.forEach((service: any) => {
        const newSrvc = { ...service };
        if (service.parent_id === parentId) {
          // Recursively add sub-services
          newSrvc.name = service.specialties;
          newSrvc.value = service.specialties;
          newSrvc.specialties_id = service.id;
          newSrvc.sub_services = mapServices(services, service.id);
          res.push(newSrvc);
        }
      });
      return res;
    };

    // Map services to specialties
    const mappedSpecialties = removedSpecialties.map((specialty: any) => {
      const newSpecialty = { ...specialty };
      newSpecialty.name = specialty?.specialties;
      newSpecialty.value = specialty?.specialties;
      newSpecialty.specialties_id = specialty.id;
      newSpecialty.services_data = mapServices(removedServices, specialty.id);
      return newSpecialty;
    });

    setDeletedSelectedSpecialties(mappedSpecialties);
  }, [ selectedSpecialties, open ]);

  return (
    <>
      <Row className="mb-3">
        <Col>
          <span className="specialties-services-title">
            {i18nReferralReason.specialtiesServices.title}
          </span>
        </Col>
      </Row>
      {loading && (<SpecialtiesServicesSkeleton />)}
      {!loading && (
        <Row className="specialties-services-container">
          <Col lg={6}>
            {specialtiesServices.length > 0
            && specialtiesServices.slice(0, Math.ceil(specialtiesServices.length / 2))
              .map((specialty: any) => (
                <Row key={specialty.id} className="mb-3">
                  <Col xs={12}>
                    <Checkbox
                      id={specialty?.id}
                      name={specialty?.id}
                      type="checkbox"
                      checked={selectedSpecialties
                        .some((s: any) => s?.specialties_id === specialty?.id
                      || s?.id === specialty?.id)}
                      label={specialty?.name}
                      onInputChangeHandler={() => handleCheckboxChange(
                        specialty, specialty?.services_data,
                      )}
                      isDisabled={isCompleted}
                      labelClassName="services-label-style"
                    />
                    {specialty?.services_data.length > 0 && selectedSpecialties
                      .some((s: any) => s?.specialties_id === specialty?.id
                    || s?.id === specialty?.id) && (
                      <Row className="services-container mb-3">
                        <Col>
                          {specialty?.services_data
                            .map((srvc: any, srvcIdx: number) => (
                              <div
                                key={`${srvcIdx.toString()}`}
                              >
                                <Checkbox
                                  id={specialty?.id}
                                  name={specialty?.id}
                                  type="checkbox"
                                  checked={selectedSpecialties
                                    .some((s: any) => s?.specialties_id === srvc?.id
                                  || s?.id === srvc?.id)}
                                  label={srvc?.name}
                                  onInputChangeHandler={() => handleCheckboxChange(
                                    srvc, srvc?.sub_services,
                                  )}
                                  isDisabled={isCompleted}
                                  labelClassName="services-label-style"
                                />
                                {srvc?.name === 'Need Post Space' && (
                                  <div className="mt-2" style={{ marginLeft: '30px' }}>
                                    <RadioInput
                                      type="radio"
                                      checked={formData[srvc?.id] === 'Yes'}
                                      onInputChangeHandler={() => handleRemarksChange(
                                        srvc?.id,
                                        'Yes',
                                      )}
                                      label={i18nReferralReason
                                        .specialtiesServices.radioOption.yes}
                                      isReadOnly={isCompleted}
                                    />
                                    <RadioInput
                                      type="radio"
                                      checked={formData[srvc?.id] === 'No'}
                                      onInputChangeHandler={() => handleRemarksChange(
                                        srvc?.id,
                                        'No',
                                      )}
                                      label={i18nReferralReason
                                        .specialtiesServices.radioOption.no}
                                      isReadOnly={isCompleted}
                                    />
                                  </div>
                                )}
                                <Row className="subServices-container">
                                  <Col>
                                    {srvc?.sub_services.length > 0 && selectedSpecialties
                                      .some((s: any) => s?.specialties_id === srvc?.id
                                    || s?.id === srvc?.id)
                                  && srvc?.sub_services
                                    .map((subSrvc: any, subSrvcIdx: number) => (
                                      <Row
                                        key={`${subSrvcIdx.toString()}`}
                                        className="mb-2"
                                      >
                                        {subSrvc?.name === 'Other' && (
                                          <>
                                            <Col xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                                              <Checkbox
                                                id={specialty?.id}
                                                name={specialty?.id}
                                                type="checkbox"
                                                checked={selectedSpecialties
                                                  .some(
                                                    (s: any) => s?.specialties_id
                                                    === subSrvc?.id || s?.id === subSrvc?.id)}
                                                label={subSrvc.name}
                                                onInputChangeHandler={
                                                  () => handleCheckboxChange(subSrvc)
                                                }
                                                isDisabled={isCompleted}
                                                labelClassName="services-label-style"
                                              />
                                            </Col>
                                            <Col xs={8}>
                                              <InputField
                                                id="specialty-remarks-input"
                                                name="specialty-remarks"
                                                customClassName="referral-reason-remarks-input"
                                                formGrpClassName="ph-form-grp"
                                                placeholder={i18nReferralReason
                                                  .specialtiesServices.input.others.placeholder}
                                                type="text"
                                                defaultValue={formData[subSrvc?.id]}
                                                value={formData[subSrvc?.id]}
                                                onInputChangeHandler={(
                                                  value,
                                                ) => handleRemarksChange(
                                                  subSrvc?.id,
                                                  value,
                                                )}
                                                float={false}
                                                fieldError={errors?.remarks}
                                                isReadOnly={isCompleted}
                                              />
                                            </Col>
                                          </>
                                        )}
                                        {subSrvc?.name !== 'Other' && (
                                          <Col>
                                            <Checkbox
                                              id={specialty?.id}
                                              name={specialty?.id}
                                              type="checkbox"
                                              checked={selectedSpecialties
                                                .some(
                                                  (s: any) => s?.specialties_id
                                                  === subSrvc?.id || s?.id === subSrvc?.id)}
                                              label={subSrvc?.name}
                                              onInputChangeHandler={
                                                () => handleCheckboxChange(subSrvc)
                                              }
                                              isDisabled={isCompleted}
                                              labelClassName="services-label-style"
                                            />
                                          </Col>
                                        )}
                                      </Row>
                                    ))}
                                  </Col>
                                </Row>
                              </div>
                            ))}
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              ))}
          </Col>
          <Col lg={6}>
            {specialtiesServices.length > 0
            && specialtiesServices.slice(Math.ceil(specialtiesServices.length / 2))
              .map((specialty: any) => (
                <Row key={specialty.id} className="mb-3">
                  <Col xs={12}>
                    <Checkbox
                      id={specialty?.id}
                      name={specialty?.id}
                      type="checkbox"
                      checked={selectedSpecialties
                        .some((s: any) => s?.specialties_id === specialty?.id
                      || s?.id === specialty?.id)}
                      label={specialty?.name}
                      onInputChangeHandler={() => handleCheckboxChange(
                        specialty, specialty?.services_data,
                      )}
                      isDisabled={isCompleted}
                      labelClassName="services-label-style"
                    />
                    {specialty?.services_data.length > 0 && selectedSpecialties
                      .some((s: any) => s?.specialties_id === specialty?.id
                    || s?.id === specialty?.id) && (
                      <Row className="services-container mb-3">
                        <Col>
                          {specialty?.services_data
                            .map((srvc: any, srvcIdx: number) => (
                              <div
                                key={`${srvcIdx.toString()}`}
                              >
                                <Checkbox
                                  id={specialty?.id}
                                  name={specialty?.id}
                                  type="checkbox"
                                  checked={selectedSpecialties
                                    .some((s: any) => s?.specialties_id === srvc?.id
                                  || s?.id === srvc?.id)}
                                  label={srvc?.name}
                                  onInputChangeHandler={() => handleCheckboxChange(
                                    srvc, srvc?.sub_services,
                                  )}
                                  isDisabled={isCompleted}
                                  labelClassName="services-label-style"
                                />
                                {srvc?.name === 'Need Post Space' && (
                                  <div className="mt-2" style={{ marginLeft: '30px' }}>
                                    <RadioInput
                                      type="radio"
                                      checked={formData[srvc?.id] === 'Yes'}
                                      onInputChangeHandler={() => handleRemarksChange(
                                        srvc?.id,
                                        'Yes',
                                      )}
                                      label={i18nReferralReason
                                        .specialtiesServices.radioOption.yes}
                                      isReadOnly={isCompleted}
                                    />
                                    <RadioInput
                                      type="radio"
                                      checked={formData[srvc?.id] === 'No'}
                                      onInputChangeHandler={() => handleRemarksChange(
                                        srvc?.id,
                                        'No',
                                      )}
                                      label={i18nReferralReason
                                        .specialtiesServices.radioOption.no}
                                      isReadOnly={isCompleted}
                                    />
                                  </div>
                                )}
                                <Row className="subServices-container">
                                  <Col>
                                    {srvc?.sub_services.length > 0 && selectedSpecialties
                                      .some((s: any) => s?.specialties_id === srvc?.id
                                    || s?.id === srvc?.id)
                                  && srvc?.sub_services
                                    .map((subSrvc: any, subSrvcIdx: number) => (
                                      <Row
                                        key={`${subSrvcIdx.toString()}`}
                                        className="mb-2"
                                      >
                                        {subSrvc?.name === 'Other' && (
                                          <>
                                            <Col xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                                              <Checkbox
                                                id={specialty?.id}
                                                name={specialty?.id}
                                                type="checkbox"
                                                checked={selectedSpecialties
                                                  .some(
                                                    (s: any) => s?.specialties_id
                                                    === subSrvc?.id || s?.id === subSrvc?.id)}
                                                label={subSrvc.name}
                                                onInputChangeHandler={
                                                  () => handleCheckboxChange(subSrvc)
                                                }
                                                isDisabled={isCompleted}
                                                labelClassName="services-label-style"
                                              />
                                            </Col>
                                            <Col xs={8}>
                                              <InputField
                                                id="specialty-remarks-input"
                                                name="specialty-remarks"
                                                customClassName="referral-reason-remarks-input"
                                                formGrpClassName="ph-form-grp"
                                                placeholder={i18nReferralReason
                                                  .specialtiesServices.input.others.placeholder}
                                                type="text"
                                                defaultValue={formData[subSrvc?.id]}
                                                value={formData[subSrvc?.id]}
                                                onInputChangeHandler={(
                                                  value,
                                                ) => handleRemarksChange(
                                                  subSrvc?.id,
                                                  value,
                                                )}
                                                float={false}
                                                fieldError={errors?.remarks}
                                                isReadOnly={isCompleted}
                                              />
                                            </Col>
                                          </>
                                        )}
                                        {subSrvc?.name !== 'Other' && (
                                          <Col>
                                            <Checkbox
                                              id={specialty?.id}
                                              name={specialty?.id}
                                              type="checkbox"
                                              checked={selectedSpecialties
                                                .some(
                                                  (s: any) => s?.specialties_id
                                                  === subSrvc?.id || s?.id === subSrvc?.id)}
                                              label={subSrvc?.name}
                                              onInputChangeHandler={
                                                () => handleCheckboxChange(subSrvc)
                                              }
                                              isDisabled={isCompleted}
                                              labelClassName="services-label-style"
                                            />
                                          </Col>
                                        )}
                                      </Row>
                                    ))}
                                  </Col>
                                </Row>
                              </div>
                            ))}
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              ))}
          </Col>
        </Row>
      )}
    </>
  );
};

export default SpecialtiesServices;
